import React from 'react'
import styled from '@emotion/styled'
import { Input } from 'antd'
import { Row, Col } from 'antd/lib/grid'
import { FormInstance, Rule } from 'antd/lib/form'
import { NamePath } from 'antd/lib/form/interface'
import dayjs from 'dayjs'
import { ClockIcon } from '@slerp/assets'
import { FormItem } from './Form'

type TimeInputField = 'start_time' | 'end_time'

/** `end_time` alone rejects 00:00 (a zero-length window); every other value must parse as a time */
const createTimeInputRule = (field: TimeInputField): Rule => ({
  validateTrigger: ['onSubmit'],
  validator: (_, value: unknown) => {
    if (field === 'end_time' && value) {
      return value === '00:00'
        ? Promise.reject(
            'This cannot be set to 00:00. Please select a different time.'
          )
        : Promise.resolve()
    }

    if (!value) {
      return Promise.reject('Time is required')
    }

    if (dayjs.isDayjs(value) && value.isValid()) {
      return Promise.resolve()
    }

    if (typeof value === 'string') {
      if (dayjs(value, 'HH:mm').isValid()) return Promise.resolve()
      if (dayjs(value, 'HH:mm:ss').isValid()) return Promise.resolve()
    }

    if (typeof value === 'number' && dayjs(value).isValid()) {
      return Promise.resolve()
    }

    return Promise.reject('Please enter a valid time')
  }
})

const handleTimeInputKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  form: FormInstance,
  namePath: NamePath
) => {
  if (e.key === 'Backspace') {
    e.preventDefault()
    form.setFieldValue(namePath, '')
  }
}

const handleTimeInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  form: FormInstance,
  namePath: NamePath
) => {
  const input = e.target.value.replace(/\D/g, '') // Remove non-digit characters
  const formattedInput = input.slice(0, 4) // Limit input to 4 digits

  let newTime = ''

  if (formattedInput.length >= 2) {
    const hoursPart = formattedInput.slice(0, 2)
    const minutesPart = formattedInput.slice(2)

    // Validate hours to ensure they do not exceed 23
    if (Number(hoursPart) > 23) {
      newTime = `23:${minutesPart}` // Set hours to 23 if exceeded
    } else {
      newTime = `${hoursPart}:${minutesPart}`
    }
  } else {
    newTime = formattedInput // If less than 2 digits, just show the input
  }

  form.setFieldValue(namePath, newTime)
}

const handleTimeInputBlur = (
  e: React.FocusEvent<HTMLInputElement>,
  form: FormInstance,
  namePath: NamePath
) => {
  const [hours, minutes] = e.target.value.split(':')
  let formattedTime = ''

  if (hours) {
    formattedTime = `${hours.padStart(2, '0')}:` // Ensure hours are two digits
    if (minutes) {
      const minutesNum = Number(minutes)
      if (minutesNum > 59) {
        formattedTime += '00' // Reset minutes to 00 if greater than 59
      } else {
        formattedTime += minutes.length === 1 ? `0${minutes}` : minutes // Ensure minutes are two digits
      }
    } else {
      formattedTime += '00' // Default minutes to 00 if not provided
    }
  } else if (!hours && !minutes) {
    formattedTime = 'HH:MM'
  }

  // Validate the final formatted time
  const [finalHours, finalMinutes] = formattedTime.split(':')
  const finalHoursNum = Number(finalHours)
  const finalMinutesNum = Number(finalMinutes)

  // Only update state if the time is valid, otherwise leave the raw input as-is
  form.setFieldValue(
    namePath,
    finalHoursNum < 24 && finalMinutesNum < 60 ? formattedTime : e.target.value
  )
}

interface TimeRangeInputProps {
  form: FormInstance
  /** Defaults to the flat `'start_time'` field name, for a single shared window */
  startName?: NamePath
  /** Defaults to the flat `'end_time'` field name, for a single shared window */
  endName?: NamePath
  disabled?: boolean
  /** Extra rules appended after the built-in `end_time` rule, e.g. an end-after-start check */
  endRules?: Rule[]
  /** Re-validates the end time whenever either time changes, for `endRules` that compare the two */
  revalidateEndOnStartChange?: boolean
  /** One compact line, `[start] to [end]`, with no From / To labels or clock icon */
  inline?: boolean
}

interface TimeFieldProps {
  form: FormInstance
  name: NamePath
  label?: string
  rules: Rule[]
  dependencies?: NamePath[]
  /** Runs after the blur handler has written the padded value */
  onAfterBlur?: () => void
  disabled?: boolean
  inline?: boolean
}

const TimeField = ({
  form,
  name,
  label,
  rules,
  dependencies,
  onAfterBlur,
  disabled,
  inline
}: TimeFieldProps) => {
  const Field = inline ? InlineTimeInput : TimeInput
  return (
    <FormItem
      className='_mb-0'
      name={name}
      label={label}
      rules={rules}
      dependencies={dependencies}
      preserve={true}
    >
      <Field
        placeholder='HH:MM'
        disabled={disabled}
        prefix={inline ? undefined : <ClockIcon height={14} width={14} />}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
          handleTimeInputKeyDown(e, form, name)
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleTimeInputChange(e, form, name)
        }
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          handleTimeInputBlur(e, form, name)
          onAfterBlur?.()
        }}
      />
    </FormItem>
  )
}

/** A From / To pair of typed `HH:MM` inputs */
export const TimeRangeInput = ({
  form,
  startName = 'start_time',
  endName = 'end_time',
  disabled,
  endRules = [],
  revalidateEndOnStartChange,
  inline
}: TimeRangeInputProps) => {
  const startRules = [createTimeInputRule('start_time')]
  const allEndRules = [createTimeInputRule('end_time'), ...endRules]
  const endDependencies = revalidateEndOnStartChange ? [startName] : undefined
  // Re-checks the end once either blur has padded its value; `setFieldValue` skips validation
  const revalidateEnd = revalidateEndOnStartChange
    ? () => form.validateFields([endName]).catch(() => undefined)
    : undefined

  if (inline) {
    return (
      <InlineRow>
        <TimeField
          form={form}
          name={startName}
          onAfterBlur={revalidateEnd}
          rules={startRules}
          disabled={disabled}
          inline
        />
        <InlineSeparator>to</InlineSeparator>
        <TimeField
          form={form}
          name={endName}
          onAfterBlur={revalidateEnd}
          rules={allEndRules}
          dependencies={endDependencies}
          disabled={disabled}
          inline
        />
      </InlineRow>
    )
  }

  return (
    <Row className='_mb-0' justify='space-between' gutter={[24, 0]}>
      <Col span={12}>
        <TimeField
          form={form}
          name={startName}
          onAfterBlur={revalidateEnd}
          label='From'
          rules={startRules}
          disabled={disabled}
        />
      </Col>
      <Col span={12}>
        <TimeField
          form={form}
          name={endName}
          onAfterBlur={revalidateEnd}
          label='To'
          rules={allEndRules}
          dependencies={endDependencies}
          disabled={disabled}
        />
      </Col>
    </Row>
  )
}

const TimeInput = styled(Input)`
  width: 100%;

  &.ant-input-affix-wrapper {
    padding: 8px 12px 8px 0;
  }

  &.ant-input-affix-wrapper-focused .ant-input {
    border: none;
  }

  .ant-input {
    position: relative;
    top: 1px;
  }

  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid #dbd9df;
  font-size: 14px;
  line-height: 21px;
  color: #8c8ca1;
  font-weight: 400;
  text-align: center;

  .ant-input-prefix {
    width: 14px;
    height: 14px;
    padding: 0;
    margin-left: 12px;

    svg {
      width: 14px;
      height: 14px;
    }

    svg path {
      fill: #8c8ca1;
    }
  }
`

const InlineRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const InlineSeparator = styled.span`
  font-size: 14px;
  line-height: 36px;
  color: #4a4a68;
`

const InlineTimeInput = styled(Input)`
  width: 100px;
  height: 36px;
  padding: 0 12px;
  border: none;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px #d2d2d9,
    inset 0 2px 2px -1px rgba(74, 74, 104, 0.1);
  font-size: 14px;
  line-height: 1.4;
  color: #4a4a68;
  text-align: center;
  transition: box-shadow 200ms ease;

  &:hover,
  &:focus {
    box-shadow: inset 0 0 0 1px #77778e,
      inset 0 2px 2px -1px rgba(74, 74, 104, 0.1);
  }
`
